import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useState, useContext, useEffect } from "react";
import { CommonContext } from "../../contexts/CommonContext";
import { useNavigate } from "react-router-dom";
import ComponentLoader from "../../components/ComponentLoader";
import { Helmet } from "react-helmet";
import NavHeader from "../../components/NavHeader";
import { motion } from "framer-motion";
import {
  getPilotCart,
  updatePilotCartQuantity,
  removeFromPilotCart,
  clearPilotCart,
  generateCartItemId,
} from "../../utils/pilotUtils";
import {
  Typography,
  IconButton,
  Divider,
  Paper,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

/** Sum of additionalPrice across all selected customization options (selections / legacy arrays / single). */
function sumCustomizationAdditional(customizations) {
  if (!customizations || typeof customizations !== "object") return 0;
  return Object.values(customizations).reduce((sum, val) => {
    if (val && Array.isArray(val.selections)) {
      return (
        sum +
        val.selections.reduce(
          (s, o) => s + (parseFloat(o?.additionalPrice) || 0),
          0
        )
      );
    }
    if (Array.isArray(val)) {
      return (
        sum +
        val.reduce((s, o) => s + (parseFloat(o?.additionalPrice) || 0), 0)
      );
    }
    return sum + (parseFloat(val?.additionalPrice) || 0);
  }, 0);
}

/** Flatten customization map to rows for display. */
function flattenCartCustomizations(customizations) {
  if (!customizations || typeof customizations !== "object") return [];
  return Object.entries(customizations).flatMap(([key, value]) => {
    if (value && Array.isArray(value.selections)) {
      return value.selections.map((opt, idx) => ({
        id: `${key}-${opt?.id ?? opt?.sku ?? "opt"}-${idx}`,
        label: opt?.name || opt?.displayName || key,
        addOn: parseFloat(opt?.additionalPrice) || 0,
      }));
    }
    if (Array.isArray(value)) {
      return value.map((opt, idx) => ({
        id: `${key}-${opt?.id ?? opt?.sku ?? "opt"}-${idx}`,
        label: opt?.name || opt?.displayName || key,
        addOn: parseFloat(opt?.additionalPrice) || 0,
      }));
    }
    if (value && typeof value === "object" && (value.sku != null || value.id != null)) {
      return [
        {
          id: `${key}-${value?.id ?? value?.sku ?? "0"}`,
          label: value?.name || value?.displayName || key,
          addOn: parseFloat(value?.additionalPrice) || 0,
        },
      ];
    }
    return [];
  });
}

function Cart() {
  const navigate = useNavigate();
  const { isDesktop, showSnackbar } = useContext(CommonContext);

  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ products: [], totalAmount: 0, totalCount: 0 });

  const fetchCart = async () => {
    try {
      setLoading(true);
      const cartData = await getPilotCart();
      setCart(cartData);
    } catch (error) {
      console.error("Error fetching cart:", error);
      showSnackbar("Error loading cart", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const handleQuantityChange = async (product, change) => {
    const result = await updatePilotCartQuantity(product, change);
    if (result.success) {
      setCart(result.cart);
      if (change > 0) {
        showSnackbar("Quantity increased", "success");
      } else if (result.cart.products.find(item => generateCartItemId(item) === generateCartItemId(product))) {
        showSnackbar("Quantity decreased", "success");
      } else {
        showSnackbar("Item removed from cart", "success");
      }
    } else {
      showSnackbar(result.error || "Failed to update quantity", "error");
    }
  };

  const handleRemoveItem = async (product) => {
    const result = await removeFromPilotCart(product);
    if (result.success) {
      setCart(result.cart);
      showSnackbar("Item removed from cart", "success");
    } else {
      showSnackbar(result.error || "Failed to remove item", "error");
    }
  };

  const handleClearCart = async () => {
    const result = await clearPilotCart();
    if (result.success) {
      setCart({ products: [], totalAmount: 0, totalCount: 0 });
      showSnackbar("Cart cleared", "success");
    } else {
      showSnackbar(result.error || "Failed to clear cart", "error");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          padding: isDesktop ? "4vw 10vw" : "4vw",
          marginTop: "7vh",
          background: "#FAFAFA",
          minHeight: "100vh",
        }}
      >
        <ComponentLoader />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Cart - Pilot Products</title>
        <meta name="description" content="Your shopping cart" />
      </Helmet>
      <Box
        sx={{
          padding: isDesktop ? "4vw 10vw" : "4vw",
          marginTop: "7vh",
          background: "#FAFAFA",
          minHeight: "100vh",
          paddingBottom: "100px",
        }}
      >
        <NavHeader />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "32px",
            }}
          >
            <Typography
              sx={{
                fontSize: isDesktop ? "32px" : "28px",
                fontWeight: "800",
                fontFamily: "RobotoSlabBold",
                color: "#2d3436",
              }}
            >
              Your Cart
            </Typography>
            {cart.products.length > 0 && (
              <Button
                onClick={handleClearCart}
                sx={{
                  color: "#dc3545",
                  fontWeight: "600",
                  fontFamily: "RobotoSlab",
                  textTransform: "none",
                }}
              >
                Clear Cart
              </Button>
            )}
          </Box>

          {cart.products.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
                textAlign: "center",
              }}
            >
              <ShoppingCartIcon
                sx={{
                  fontSize: "80px",
                  color: "#dee2e6",
                  marginBottom: "24px",
                }}
              />
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#6c757d",
                  fontFamily: "RobotoSlab",
                  marginBottom: "12px",
                }}
              >
                Your cart is empty
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#adb5bd",
                  fontFamily: "RobotoSlab",
                  marginBottom: "32px",
                }}
              >
                Add some products to get started
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/pilot/products")}
                sx={{
                  background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                  color: "white",
                  fontWeight: "700",
                  padding: "12px 32px",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontFamily: "RobotoSlab",
                  boxShadow: "0 4px 15px rgba(164, 36, 61, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #8a1e33 0%, #e55555 100%)",
                    boxShadow: "0 6px 20px rgba(164, 36, 61, 0.4)",
                  },
                }}
              >
                Browse Products
              </Button>
            </Box>
          ) : (
            <>
              {cart.products.map((product, index) => (
                <motion.div
                  key={generateCartItemId(product)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: "14px",
                      background: "white",
                      border: "1px solid #e9ecef",
                      transition: "box-shadow 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 6px 24px rgba(0, 0, 0, 0.06)",
                      },
                    }}
                  >
                    {(() => {
                      const customRows = flattenCartCustomizations(
                        product.customizations
                      );
                      const showAddOnCol = customRows.some((r) => r.addOn > 0);
                      const custExtra = sumCustomizationAdditional(
                        product.customizations
                      );
                      const variantBase = parseFloat(product.variant?.price);
                      const priceMatchesParts =
                        custExtra > 0 &&
                        variantBase > 0 &&
                        Math.abs(
                          variantBase + custExtra - Number(product.price)
                        ) < 0.02;

                      return (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              width: isDesktop ? 108 : 88,
                              height: isDesktop ? 108 : 88,
                              borderRadius: "10px",
                              overflow: "hidden",
                              background: "#f1f3f5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/300x200?text=No+Image";
                                }}
                              />
                            ) : (
                              <ShoppingCartIcon
                                sx={{ fontSize: 36, color: "#dee2e6" }}
                              />
                            )}
                          </Box>

                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: isDesktop ? 17 : 16,
                                  fontWeight: 700,
                                  fontFamily: "RobotoSlab",
                                  color: "#2d3436",
                                  lineHeight: 1.35,
                                  cursor: "pointer",
                                  flex: 1,
                                  minWidth: 0,
                                  pr: 0.5,
                                  "&:hover": { color: "#a4243d" },
                                }}
                                onClick={() =>
                                  navigate(
                                    `/pilot/products/${product.productId}`
                                  )
                                }
                              >
                                {product.name}
                              </Typography>
                              <IconButton
                                size="small"
                                aria-label="Remove item"
                                onClick={() => handleRemoveItem(product)}
                                sx={{
                                  color: "#dc3545",
                                  flexShrink: 0,
                                  mt: -0.25,
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            {product.variant &&
                              Object.keys(product.variant).length > 0 && (
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    color: "#868e96",
                                    fontFamily: "RobotoSlab",
                                    lineHeight: 1.4,
                                    mt: -0.5,
                                  }}
                                >
                                  {[
                                    product.variant.weight,
                                    product.variant.name ||
                                      product.variant.sku,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </Typography>
                              )}

                            {customRows.length > 0 && (
                              <Box
                                sx={{
                                  pl: 1.25,
                                  borderLeft: "3px solid #e9ecef",
                                  py: 0.25,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: "#adb5bd",
                                    fontFamily: "RobotoSlab",
                                    mb: 0.75,
                                  }}
                                >
                                  Add-ons
                                </Typography>
                                <Box
                                  component="ul"
                                  sx={{
                                    m: 0,
                                    p: 0,
                                    listStyle: "none",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5,
                                  }}
                                >
                                  {customRows.map((row) => (
                                    <Box
                                      component="li"
                                      key={row.id}
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "baseline",
                                        gap: 2,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          color: "#495057",
                                          fontFamily: "RobotoSlab",
                                          lineHeight: 1.35,
                                        }}
                                      >
                                        {row.label}
                                      </Typography>
                                      {showAddOnCol && (
                                        <Typography
                                          sx={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            fontFamily: "RobotoSlab",
                                            color:
                                              row.addOn > 0
                                                ? "#a4243d"
                                                : "#ced4da",
                                            flexShrink: 0,
                                          }}
                                        >
                                          {row.addOn > 0
                                            ? `+₹${row.addOn}`
                                            : "—"}
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}

                            <Divider sx={{ borderColor: "#f1f3f5" }} />

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-end",
                                gap: 2,
                                flexWrap: "wrap",
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 0.75,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 22,
                                      fontWeight: 800,
                                      fontFamily: "RobotoSlabBold",
                                      color: "#a4243d",
                                      lineHeight: 1,
                                    }}
                                  >
                                    ₹{product.price}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: "#adb5bd",
                                      fontFamily: "RobotoSlab",
                                    }}
                                  >
                                    / unit
                                  </Typography>
                                </Box>
                                {custExtra > 0 && (
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: "#868e96",
                                      fontFamily: "RobotoSlab",
                                      mt: 0.5,
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {priceMatchesParts
                                      ? `₹${variantBase} + ₹${custExtra} add-ons`
                                      : `Includes ₹${custExtra} in add-ons`}
                                  </Typography>
                                )}
                              </Box>

                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  borderRadius: "10px",
                                  border: "1px solid #dee2e6",
                                  bgcolor: "#fafafa",
                                  p: "2px",
                                  flexShrink: 0,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleQuantityChange(product, -1)
                                  }
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    color: "#a4243d",
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography
                                  sx={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: "#2d3436",
                                    fontFamily: "RobotoSlab",
                                    minWidth: 28,
                                    textAlign: "center",
                                  }}
                                >
                                  {product.quantity}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleQuantityChange(product, 1)
                                  }
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    color: "#a4243d",
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })()}
                  </Paper>
                </motion.div>
              ))}

              {/* Order Summary */}
              <Paper
                elevation={0}
                sx={{
                  padding: "24px",
                  borderRadius: "16px",
                  background: "white",
                  border: "2px solid #a4243d",
                  marginTop: "32px",
                  position: "sticky",
                  bottom: 20,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: "700",
                      fontFamily: "RobotoSlab",
                      color: "#2d3436",
                    }}
                  >
                    Total Items:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: "700",
                      fontFamily: "RobotoSlab",
                      color: "#2d3436",
                    }}
                  >
                    {cart.totalCount}
                  </Typography>
                </Box>
                <Divider sx={{ marginBottom: "20px" }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "24px",
                      fontWeight: "800",
                      fontFamily: "RobotoSlabBold",
                      color: "#2d3436",
                    }}
                  >
                    Total Amount:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "28px",
                      fontWeight: "800",
                      fontFamily: "RobotoSlabBold",
                      color: "#a4243d",
                    }}
                  >
                    ₹{cart.totalAmount.toFixed(2)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                    color: "white",
                    fontWeight: "700",
                    padding: "14px",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontFamily: "RobotoSlab",
                    fontSize: "16px",
                    boxShadow: "0 4px 15px rgba(164, 36, 61, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #8a1e33 0%, #e55555 100%)",
                      boxShadow: "0 6px 20px rgba(164, 36, 61, 0.4)",
                    },
                  }}
                  onClick={() => {
                    navigate("/pilot/delivery");
                  }}
                >
                  Proceed to Checkout
                </Button>
              </Paper>
            </>
          )}
        </motion.div>
      </Box>
    </>
  );
}

export default Cart;
